/**
 * Cookie-based RS256 auth client.
 *
 * Authentication state lives in httpOnly cookies (mp_access, mp_refresh) that
 * the browser handles automatically. CSRF is double-submit via the JS-readable
 * mp_csrf cookie + X-CSRF-Token header (added by apiService for writes).
 */
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || 'http://localhost:5700';

const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

http.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    const csrf = readCookie('mp_csrf');
    if (csrf) {
      config.headers = config.headers || {};
      (config.headers as any)['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginSuccessResponse {
  requires_2fa: false;
  user: AuthUser;
}

export interface LoginNeeds2FAResponse {
  requires_2fa: true;
  pending_id: string;
  method: string;
}

export type LoginResponse = LoginSuccessResponse | LoginNeeds2FAResponse;

export interface TwoFactorRequest {
  pending_id: string;
  code: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  phone: string;
  first_name: string;
  last_name: string;
}

export interface SignupResponse {
  message: string;
  email: string;
  needs_verification: boolean;
}

export interface VerifyPhoneRequest {
  email: string;
  code: string;
}

export interface AuthUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  is_admin: boolean;
  phone_verified: boolean;
  email_verified: boolean;
  two_factor_enabled: boolean;
  two_factor_method: string;
  auth_v2_enrolled: boolean;
}

class AuthService {
  async login(payload: LoginRequest): Promise<LoginResponse> {
    const response = await http.post<LoginResponse>('/api/auth/login', payload);
    return response.data;
  }

  async verify2FA(payload: TwoFactorRequest): Promise<{ user: AuthUser }> {
    const response = await http.post<{ user: AuthUser }>('/api/auth/2fa', payload);
    return response.data;
  }

  async refresh(): Promise<{ user: AuthUser }> {
    const response = await http.post<{ user: AuthUser }>('/api/auth/refresh');
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      // Let the server clear httpOnly cookies before callers navigate away.
      await http.post('/api/auth/logout', undefined, { timeout: 5000 });
    } catch {
      // Even if logout fails, the SPA should treat session as gone.
    }
  }

  async me(): Promise<{ user: AuthUser }> {
    const response = await http.get<{ user: AuthUser }>('/api/auth/me');
    return response.data;
  }

  async forgotPassword(payload: ForgotPasswordRequest): Promise<{ ok: boolean }> {
    const response = await http.post<{ ok: boolean }>('/api/auth/forgot-password', payload);
    return response.data;
  }

  async resetPassword(payload: ResetPasswordRequest): Promise<{ ok: boolean }> {
    const response = await http.post<{ ok: boolean }>('/api/auth/reset-password', payload);
    return response.data;
  }

  async changePassword(payload: ChangePasswordRequest): Promise<{ ok: boolean }> {
    const response = await http.post<{ ok: boolean }>('/api/auth/change-password', payload);
    return response.data;
  }

  async acceptInvitation(
    token: string,
    newPassword: string,
    firstName?: string,
    lastName?: string,
    email?: string,
  ): Promise<{ ok: boolean; user_id: number; client_id: number }> {
    const response = await http.post('/api/invitations/accept', {
      token,
      new_password: newPassword,
      email,
      first_name: firstName,
      last_name: lastName,
    });
    return response.data;
  }

  async signup(payload: SignupRequest): Promise<SignupResponse> {
    const response = await http.post<SignupResponse>('/api/auth/signup', payload);
    return response.data;
  }

  async verifyPhone(payload: VerifyPhoneRequest): Promise<{ user: AuthUser } | { access_token?: string; user: AuthUser }> {
    // Legacy custom_auth.py returns access_token + user. New auth_v2 phone-verify
    // would set cookies and return {user}. We accept either shape.
    const cleanCode = payload.code.replace(/\D/g, '').trim();
    const response = await http.post('/api/auth/verify-phone', {
      email: payload.email.trim().toLowerCase(),
      code: cleanCode,
    });
    return response.data;
  }

  // ---------- Backward-compat shims for legacy UserContext.tsx -----------
  //
  // Cookie auth has no client-readable token. These shims preserve the
  // pre-cookie API surface so existing callers compile and behave sensibly:
  //   - getToken: returns null (no bearer to surface)
  //   - isAuthenticated: best-effort — checks the JS-readable mp_csrf
  //     cookie (mp_access and mp_refresh are httpOnly so the browser hides
  //     them from JS). The server enforces actual validity on /me.
  //   - clearToken: NO-OP. Cookies are httpOnly, so JS can't clear them
  //     anyway. Legacy callers in UserContext invoke clearToken on
  //     transient 401s and session-expiration checks; if we converted that
  //     to a real server logout, every refresh-with-stale-state would kill
  //     the session. Real logout flows through authService.logout() from
  //     UserContext.logout() directly.
  // Plan: refactor UserContext to call me()/refresh() directly and remove
  // these shims.
  getToken(): string | null {
    return null;
  }

  isAuthenticated(): boolean {
    return !!readCookie('mp_csrf');
  }

  clearToken(): void {
    // Intentional no-op. See comment block above.
  }
}

export const authService = new AuthService();
